import { Box, Divider, Stack, useTheme } from "@mui/material";
import { CustomTypography } from "../../../../../library/base";
import { useGlobalStore } from "controller/store/globalStore";
import { useNavigate } from "react-router-dom";
import { TopBarProfileButton } from "../TopBarProfileButton";
import { useLogoutAccount, useSetFeatureFlagAsSeen } from "controller";
import { resetHeapIdentity, Sentry } from "utilz";
import {
  AiHomeIcon,
  BusinessUpIcon,
  LegacyHomeIcon,
  LogoutIcon,
  StarIcon,
  UserCircleIcon,
  DiscordIcon,
  ReleaseNotesIcon,
  UpArrowCircleIcon,
} from "components";
import {
  getAiHomeSession,
  timeInSessionMs,
  trackAiHome,
} from "pages/modules/home-v2/analytics";
import { useHomeViewMode } from "pages/modules/home-v2/hooks/useHomeViewMode";
import { NewFeaturePill } from "./NewFeaturePill";
import { ProfileModalOption } from "./ProfileModalOption";
import { resetAllStores } from "controller/store/resetStoreHelper";
import { useLogto } from "@logto/react";
const signoutBroadcast = new BroadcastChannel("signout_channel");
function ProfileModal({ handleClose }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const resetProfile = useGlobalStore((state) => state.resetCredentials);
  const profileName = useGlobalStore((state) => state.name) || "";
  const email = useGlobalStore((state) => state.email);
  const isHelenaActive = useGlobalStore((state) => state.isHelenaActive);
  const { mutateAsync: logoutAccount, isLoading: isLogoutLoading } =
    useLogoutAccount();
  const { mutate: setFeatureAsSeen } = useSetFeatureFlagAsSeen();
  const { signOut } = useLogto();
  const {
    mode: homeViewMode,
    setMode: setHomeViewMode,
    isUpdating,
  } = useHomeViewMode();
  const isAiHome = homeViewMode === "ai";
  const signOutHandler = async () => {
    try {
      setFeatureAsSeen("whats_new");
      const res = await logoutAccount();
      resetAllStores();
      if (
        res &&
        res?.data &&
        res?.data?.success === true &&
        res?.data?.statusCode === 200
      ) {
        signoutBroadcast.postMessage("signout");
        // clear sentry scope
        Sentry.setUser(null);
        sessionStorage.clear();
        localStorage.clear();
      }
      resetHeapIdentity();
      signOut(`${window.location.origin}/login`);
    } catch (e) {
      resetAllStores();
      if (e?.message === "unauthorized") {
        signoutBroadcast.postMessage("signout");
        sessionStorage.clear();
        localStorage.clear();
      }
      resetHeapIdentity();
      signOut(`${window.location.origin}/login`);
    }
  };

  const homeScreenSwitchOption = {
    label: isAiHome ? "Use legacy home screen" : "Use AI home screen",
    icon: isAiHome ? (
      <LegacyHomeIcon
        color="inherit"
        sx={{ width: "100%", height: "100%" }}
      />
    ) : (
      <AiHomeIcon color="inherit" sx={{ width: "100%", height: "100%" }} />
    ),
    hasRightArrowIcon: false,
    badge: isAiHome ? undefined : <NewFeaturePill />,
    onClick: () => {
      if (isUpdating) {
        return;
      }

      trackAiHome("ai_screen_toggled", {
        direction: isAiHome ? "ai_to_legacy" : "legacy_to_ai",
        time_in_session_ms: timeInSessionMs(),
        query_count_before_toggle: getAiHomeSession().queryCount,
      });

      if (isAiHome) {
        trackAiHome("use_legacy_screen_clicked", {
          source: "profile_menu",
          current_screen: homeViewMode,
        });
      } else {
        trackAiHome("use_ai_screen_clicked", {
          source: "profile_menu",
          current_screen: homeViewMode,
        });
      }

      setHomeViewMode(isAiHome ? "legacy" : "ai");
      navigate("/app/home");
    },
  };

  const ProfileModalOptions = [
    // home screen switch — always offers the experience that is not active
    ...(isHelenaActive ? [homeScreenSwitchOption] : []),
    //my account option
    {
      label: "My account",
      icon: (
        <UserCircleIcon
          color="inherit"
          sx={{ width: "100%", height: "100%" }}
        />
      ),
      onClick: () => navigate("profile"),
    },
    //discord for the support desk
    {
      label: "Discord community",
      icon: (
        <DiscordIcon color="inherit" sx={{ width: "100%", height: "100%" }} />
      ),
      onClick: () =>
        window.open("https://discord.gg/dUGXkHuJ5n", "_blank"),
    },
    {
      label: "Report issues",
      icon: <StarIcon color="inherit" sx={{ width: "100%", height: "100%" }} />,
      onClick: () =>
        window.open(
          "https://deskday.canny.io/reported-issues",
          "_blank"
        ),
    },
    {
      label: "Release notes",
      icon: (
        <ReleaseNotesIcon
          color="inherit"
          sx={{ width: "100%", height: "100%" }}
        />
      ),
      onClick: () =>
        window.open(
          "https://deskday.com/resource-center/#release-notes",
          "_blank"
        ),
    },
    {
      label: "Feature requests",
      icon: (
        <UpArrowCircleIcon
          color="inherit"
          sx={{ width: "100%", height: "100%" }}
        />
      ),
      onClick: () =>
        window.open("https://deskday.canny.io/feature-requests", "_blank"),
    },
    {
      label: "Roadmap",
      icon: (
        <BusinessUpIcon
          color="inherit"
          sx={{ width: "100%", height: "100%" }}
        />
      ),
      onClick: () => {
        window.open("https://deskday.com/our-roadmap/", "_blank");
      },
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "600px",
        backgroundColor: "white",
        overflow: "scroll",
        zIndex: "999",
        boxShadow:
          "0px 0px 8px 0px rgba(0, 0, 0, 0.16), 0px 0px 2px 0px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={"24px"}
        p={"24px"}
        sx={{
          backgroundColor: "#E7E9FE",
          cursor: "pointer",
        }}
        onClick={() => {
          navigate("profile");
          handleClose();
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={"16px"}
        >
          <TopBarProfileButton isClickble={false} status={true} />
          <Stack alignItems="start">
            <CustomTypography
              size="SMALL-BETA"
              variant="BOLD"
              content={profileName}
            />
            <CustomTypography
              size="EXTRA-SMALL"
              variant="REGULAR"
              color="SECONDARY-MAIN"
              content={email}
            />
          </Stack>
        </Stack>
      </Stack>
      {ProfileModalOptions.map(
        ({ label, icon, onClick, hasRightArrowIcon = true, badge }) => (
          <ProfileModalOption
            key={label}
            label={label}
            icon={icon}
            hasRightArrowIcon={hasRightArrowIcon}
            badge={badge}
            onClick={() => {
              onClick();
              handleClose();
            }}
          />
        ),
      )}
      <Divider color={theme.palette.neutral["015"]} />
      <ProfileModalOption
        label={"Logout"}
        icon={
          <LogoutIcon color="inherit" sx={{ width: "100%", height: "100%" }} />
        }
        hasRightArrowIcon={false}
        onClick={() => !isLogoutLoading && signOutHandler()}
        isLoading={isLogoutLoading}
      />
    </Box>
  );
}

const deleteCookies = () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
};

export { ProfileModal };
